import PropTypes from "prop-types";
import styled, { css } from "styled-components";
import propTypes from "@styled-system/prop-types";
import themeGet from "@styled-system/theme-get";
import { space, flexbox, typography } from "styled-system";
import { MultiSelect } from "react-multi-select-component";

const invalidIcon = "data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='none' stroke='%23d93737' viewBox='0 0 12 12'%3e%3ccircle cx='6' cy='6' r='4.5'/%3e%3cpath stroke-linejoin='round' d='M5.8 3.6h.4L6 6.5z'/%3e%3ccircle cx='6' cy='8.2' r='.6' fill='%23d93737' stroke='none'/%3e%3c/svg%3e";

const FormBaseStyle = css`
  display: block;
  width: 100%;
  height: 35px;
  padding: 6px 12px;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5;
  background-clip: padding-box;
  color: ${themeGet("colors.input.color", "#768192")};
  background-color: ${themeGet("colors.input.background", "#fff")};
  border: 1px solid ${themeGet("colors.input.borderColor", "#d8dbe0")};
  border-radius: 4px;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

  &:focus {
    color: ${themeGet("colors.input.focus.color", "#768192")};
    background-color: ${themeGet("colors.input.focus.backgroundColor", "#fff")};
    outline: 0;
    border-color: ${themeGet("colors.input.focus.borderColor", "#1b4aa8")};
    box-shadow: 0 0 0 3px rgb(39 93 168 / 50%);
  }

  // Placeholder
  &::placeholder {
    color: ${themeGet("colors.input.placeholder.color", "#8a93a2")};
    opacity: 1;
  }
  
  &:disabled,
  &[readonly] {
    background-color: ${themeGet("colors.input.disabled.backgroundColor", "#d8dbe0")};
    opacity: 1;
  }
  
  &[type="date"],
  &[type="time"],
  &[type="datetime-local"],
  &[type="month"] {
    appearance: none;
  }

  &[type="file"] {
    display: block;
    width: 100%;
  }
`;

const FormInputPlainText = css`
  display: block;
  width: 100%;
  padding: 6px 0;
  margin-bottom: 0;
  font-size: 14px;
  line-height: 1.5;
  background-color: transparent;
  border: solid transparent;
  border-width: 1px 0;
  color: ${themeGet("colors.body.color", "#3c4b64")};
`;

const FormInputWrapper = styled("div", {
  inline: PropTypes.bool,
  ...propTypes.space,
  ...propTypes.flexbox,
})`
  margin-bottom: 16px;

  ${({ inline }) => inline && css`
    width: 100%;
    margin-right: 16px;

    display: inline;
    margin-bottom: 0;
    
    ${themeGet("mediaQueries.smallUp")} {
      ${FormLabelStyle} {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 0;
        margin-right: 4px;
      }
  
      ${FormInputStyle},
      ${FormSelectStyle} {
        display: inline-block;
        width: auto;
        vertical-align: middle;
      }
  
      ${FormInputGroupStyle}, 
      ${FormSelectStyle} {
        width: auto;
      }
    }
  `}

  /* Style system support */
  ${space}
  ${flexbox}
`;

const FormLabelStyle = styled("label", {
  invalid: PropTypes.bool,
})`
  display: inline-block;
  margin-bottom: 8px;
  
  ${({ invalid }) => invalid && css`
    color: ${themeGet("colors.checkbox.invalid.color", "#d93737")};
  `}
`;

const FormInputGroupStyle = styled("div", {})`
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  width: 100%;
`;

const FormInputStyle = styled("input", {
  invalid: PropTypes.bool,
  hide: PropTypes.bool,
  plainText: PropTypes.bool,
  ...propTypes.space,
})`
  ${FormBaseStyle}
  
  ${({ plainText }) => plainText && FormInputPlainText}
  
  ${FormInputGroupStyle} > & {
    position: relative;
    flex: 1 1 auto;
    width: 1%;
    min-width: 0;
    margin-bottom: 0;

    &:not(:last-child) {
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
    }
  }

  ${({ invalid }) => invalid && css`
    border-color: ${themeGet("colors.input.invalid.borderColor", "#d93737")};
    background-image: url("${invalidIcon}");
    background-repeat: no-repeat;
    background-size: 16.5px 16.5px;
    padding-right: 33px;
    background-position: right 8.25px center;
    
    &:focus {
      border-color: ${themeGet("colors.input.invalid.borderColor", "#d93737")};
      box-shadow: 0 0 0 3px rgb(229 83 83 / 25%);
    }
  `}
  
  ${({ hide }) => hide && css`display: none !important;`}
    
  /* Style system support */
  ${space}
`;

const FormCheckboxWrapper = styled("div", {
  inline: PropTypes.bool,
  ...propTypes.space,
})`
  position: relative;
  display: block;
  min-height: 21px;

  ${({ inline }) => !inline && css`
    margin-bottom: 24px;
  `}

  ${({ inline }) => inline && css`
    display: inline-flex;
    flex-wrap: wrap;
    margin-right: 24px;
    padding-left: 24px;
  `}

  /* Style system support */
  ${space}
`;

const FormCheckboxLabelStyle = styled(FormLabelStyle, {
  inline: PropTypes.bool,
  invalid: PropTypes.bool,
})`
  position: absolute;
  line-height: 2;
  padding-left: 8px;
  
  &::before {
    display: block;
    width: 24px;
    height: 24px;
    margin-bottom: 0;
    pointer-events: none;
    content: "";
    background-color: ${themeGet("colors.checkbox.backgroundColor", "#fff")};
    border: 1px solid ${themeGet("colors.checkbox.borderColor", "#9da5b1")};
    border-radius: 4px;
    transition: background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  }
  
  &::after {
    position: absolute;
    top: 0;
    display: block;
    width: 24px;
    height: 24px;
    content: "";
    background: no-repeat 50% / 50% 50%;
  }
  
  ${({ inline }) => inline && css`
    position: relative;
    margin-bottom: 0;
    vertical-align: top;
    
    &::before, &::after {
      top: 2.5px;
      left: -24px;
    }

    &::before {
      position: absolute;
    }
  `}
  
  ${({ invalid }) => invalid && css`
    &::before {
      background-color: ${themeGet("colors.checkbox.invalid.backgroundColor", "#fff")};
      border: 1px solid ${themeGet("colors.checkbox.invalid.borderColor", "#d93737")};
    }
  `}
`;

const FormCheckboxStyle = styled("input", {
  inline: PropTypes.bool,
})`
  display: block;
  z-index: -1;
  width: 0;
  height: 0;
  opacity: 0;
  box-sizing: border-box;
  padding: 0;

  ${({ inline }) => inline && css`
    position: absolute;
    left: 0;
    display: inline-block;
    width: 24px;
    height: 26.5px;
  `}
  
  &:checked ~ ${FormCheckboxLabelStyle} {
    &::before {
      color: ${themeGet("colors.checkbox.checked.color", "#fff")};
      border-color: ${themeGet("colors.checkbox.checked.borderColor", "#1b3ba8")};
      background-color: ${themeGet("colors.checkbox.checked.backgroundColor", "#1b3ba8")};
    }
    
    &::after {
      background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='8' height='8' viewBox='0 0 8 8'%3e%3cpath fill='%23fff' d='M6.564.75l-3.59 3.612-1.538-1.55L0 4.26l2.974 2.99L8 2.193z'/%3e%3c/svg%3e");
    }
  }
  
  &:focus {
    ~ ${FormCheckboxLabelStyle} {
      &::before {
        box-shadow: 0 0 0 3px rgb(39 93 168 / 50%);
      }
    }
    
    &:not(:checked) ~ ${FormCheckboxLabelStyle} {
      &::before {
        border-color: ${themeGet("colors.checkbox.indeterminate.borderColor", "#1b3ba8")};
      }
    }
  }

  &:indeterminate ~ ${FormCheckboxLabelStyle} {
    &::before {
      border-color: ${themeGet("colors.checkbox.indeterminate.borderColor", "#1b3ba8")};
    }
    
    &::after {
      background-image: url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' width='4' height='4' viewBox='0 0 4 4'><path stroke='#{$custom-checkbox-indicator-indeterminate-color}' d='M0 2h4'/></svg>");
    }
  }
  
  &:not(:disabled):active {
    ~ ${FormCheckboxLabelStyle} {
      &::before {
        color: ${themeGet("colors.checkbox.active.color", "#fff")};
        background-color: ${themeGet("colors.checkbox.active.backgroundColor", "hsl(226,72%,88%)")};
        border-color: ${themeGet("colors.checkbox.active.borderColor", "hsl(226,72%,88%)")};
      }
    }
  }
  
  &[disabled],
  &:disabled {
    ~ ${FormCheckboxLabelStyle} {
      color: ${themeGet("colors.checkbox.disabled.color", "#8a93a2")};
      
      &::before {
        background-color: ${themeGet("colors.checkbox.disabled.backgroundColor", "#d8dbe0")};
      }
    }
  }

  &:disabled {
    &:checked,
    &:indeterminate {
      ~ ${FormCheckboxLabelStyle} {
        &::before {
          background-color: ${themeGet("colors.checkbox.checked.disabledBackgroundColor", "hsl(226,72%,88%)")};
        }
      }
    }
  }
`;

const FormRadioGroup = styled("div", {
  ...propTypes.space,
  ...propTypes.typography,
})`
  /* Style system support */
  ${space}
  ${typography}
`;

const FormRadioWrapper = styled("div", {
  inline: PropTypes.bool,
  ...propTypes.space,
})`
  position: relative;
  display: block;
  min-height: 21px;

  ${({ inline }) => !inline && css`
    margin-bottom: 16px;
  `}

  ${({ inline }) => inline && css`
    display: inline-flex;
    flex-wrap: wrap;
    margin-right: 24px;
    padding-left: 24px;
  `}

  /* Style system support */
  ${space}
`;

const FormRadioLabelStyle = styled(FormLabelStyle, {
  inline: PropTypes.bool,
  invalid: PropTypes.bool,
})`
  position: absolute;
  line-height: 2;
  
  &::before {
    display: block;
    width: 24px;
    height: 24px;
    margin-bottom: 0;
    pointer-events: none;
    content: "";
    background-color: ${themeGet("colors.radio.backgroundColor", "#fff")};
    border: 1px solid ${themeGet("colors.radio.borderColor", "#9da5b1")};
    border-radius: 50%;
    transition: background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  }
  
  &::after {
    position: absolute;
    top: 0;
    display: block;
    width: 24px;
    height: 24px;
    content: "";
    background: no-repeat 50% / 50% 50%;
  }
  
  ${({ inline }) => inline && css`
    position: relative;
    margin-bottom: 0;
    vertical-align: top;
    
    &::before, &::after {
      top: 2.5px;
      left: -32px;
    }

    &::before {
      position: absolute;
    }
  `}
  
  ${({ invalid }) => invalid && css`
    &::before {
      background-color: ${themeGet("colors.radio.invalid.backgroundColor", "#fff")};
      border: 1px solid ${themeGet("colors.radio.invalid.borderColor", "#d93737")};
    }
  `}
`;

const FormRadioStyle = styled("input", {
  inline: PropTypes.bool,
})`
  display: block;
  z-index: -1;
  width: 0;
  height: 0;
  opacity: 0;
  box-sizing: border-box;
  padding: 0;

  ${({ inline }) => inline && css`
    position: absolute;
    left: 0;
    display: inline-block;
    width: 24px;
    height: 26.5px;
  `}
  
  &:checked ~ ${FormRadioLabelStyle} {
    &::before {
      color: ${themeGet("colors.radio.checked.color", "#fff")};
      border-color: ${themeGet("colors.radio.checked.borderColor", "#1b3ba8")};
      background-color: ${themeGet("colors.radio.checked.backgroundColor", "#1b3ba8")};
    }
    
    &::after {
      background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='%23fff'/%3e%3c/svg%3e");
    }
  }
  
  &:focus {
    ~ ${FormRadioLabelStyle} {
      &::before {
        box-shadow: 0 0 0 3px rgb(39 93 168 / 50%);
      }
    }
    
    &:not(:checked) ~ ${FormRadioLabelStyle} {
      &::before {
        border-color: ${themeGet("colors.radio.indeterminate.borderColor", "#1b3ba8")};
      }
    }
  }
  
  &:not(:disabled):active {
    ~ ${FormRadioLabelStyle} {
      &::before {
        color: ${themeGet("colors.radio.active.color", "#fff")};
        background-color: ${themeGet("colors.radio.active.backgroundColor", "hsl(226,72%,88%)")};
        border-color: ${themeGet("colors.radio.active.borderColor", "hsl(226,72%,88%)")};
      }
    }
  }
  
  &[disabled],
  &:disabled {
    ~ ${FormRadioLabelStyle} {
      color: ${themeGet("colors.radio.disabled.color", "#8a93a2")};
      
      &::before {
        background-color: ${themeGet("colors.radio.disabled.backgroundColor", "#d8dbe0")};
      }
    }
  }

  &:disabled {
    &:checked {
      ~ ${FormRadioLabelStyle} {
        &::before {
          background-color: ${themeGet("colors.radio.checked.disabledBackgroundColor", "hsl(226,72%,88%)")};
        }
      }
    }
  }
`;

const FormInputGroupAppendStyle = styled("div", {})`
  display: flex;
  white-space: nowrap;
  vertical-align: middle;
  margin-left: -1px;
`;

const FormTextAreaStyle = styled("textarea", {
  invalid: PropTypes.bool,
  ...propTypes.space,
})`
  ${FormBaseStyle}

  height: auto;

  ${FormInputGroupStyle} > & {
    position: relative;
    flex: 1 1 auto;
    width: 1%;
    min-width: 0;
    margin-bottom: 0;

    &:not(:last-child) {
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
    }
  }

  ${({ invalid }) => invalid && css`
    border-color: ${themeGet("colors.input.invalid.borderColor", "#d93737")};
    background-image: url("${invalidIcon}");
    background-repeat: no-repeat;
    background-size: 16.5px 16.5px;
    padding-right: 33px;
    background-position: right 8.25px center;
    
    &:focus {
      border-color: ${themeGet("colors.input.invalid.borderColor", "#d93737")};
      box-shadow: 0 0 0 3px rgb(229 83 83 / 25%);
    }
  `}
    
  /* Style system support */
  ${space}
`;

const FormSelectStyle = styled("select", {})`
  display: inline-block;
  width: 100%;
  height: 35px;
  padding: 6px 28px 6px 12px;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5;
  vertical-align: middle;
  border: 1px solid ${themeGet("colors.input.borderColor", "#d8dbe0")};
  border-radius: 4px;
  -webkit-appearance: none;
  appearance: none;
  color: ${themeGet("colors.input.color", "#768192")};
  background: ${themeGet("colors.input.backgroundColor", "#fff")} url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='4' height='5' viewBox='0 0 4 5'%3e%3cpath fill='${encodeURIComponent("#636f83")}' d='M2 0L0 2h4zm0 5L0 3h4z'/%3e%3c/svg%3e") no-repeat right 12px center/8px 10px;
  
  &:focus {
    border-color: ${themeGet("colors.input.focus.borderColor", "#1b4aa8")};
    outline: 0;
    box-shadow: 0 0 0 3px rgb(39 93 168 / 50%);
  }

  &::-ms-expand {
    background-color: ${themeGet("colors.transparent", "transparent")};
    border: 0;
  }

  &:-moz-focusring {
    color: transparent;
    text-shadow: 0 0 0 ${themeGet("colors.input.focus.color", "#768192")};
  }

  &:focus::-ms-value {
    color: ${themeGet("colors.input.focus.color", "#768192")};
    background-color: ${themeGet("colors.input.focus.backgroundColor", "#fff")};
  }
  
  &:disabled {
    color: ${themeGet("colors.input.disabled.color", "#8a93a2")};
    background-color: ${themeGet("colors.input.disabled.backgroundColor", "#d8dbe0")};
  }

  &[size],
  &[multiple] {
    height: auto;
  }

  transition: background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

  ${({ invalid }) => invalid && css`
    border-color: ${themeGet("colors.input.invalid.borderColor", "#d93737")};
    background: url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' width='4' height='5' viewBox='0 0 4 5'><path fill='#636f83' d='M2 0L0 2h4zm0 5L0 3h4z'/></svg>") no-repeat right 12px center/8px 10px, url(${invalidIcon}) #fff no-repeat center right 28px/16.5px 16.5px;
    padding-right: 47.5px;
    
    &:focus {
      border-color: ${themeGet("colors.input.invalid.borderColor", "#d93737")};
      box-shadow: 0 0 0 3px rgb(229 83 83 / 25%);
    }
  `}
`;

const FormMultiSelectStyle = styled(MultiSelect)`
  &.rmsc {
    .dropdown-container {
      border: 1px solid ${themeGet("colors.input.borderColor", "#d8dbe0")};
      border-radius: 4px;
      -webkit-appearance: none;
      appearance: none;
      background: ${themeGet("colors.input.backgroundColor", "#fff")};
  
      &:focus,
      &:focus-within {
        border-color: ${themeGet("colors.input.focus.borderColor", "#1b4aa8")};
        outline: 0;
        box-shadow: 0 0 0 3px rgb(39 93 168 / 50%);
      }
  
      &:-moz-focusring {
        color: transparent;
        text-shadow: 0 0 0 ${themeGet("colors.input.focus.color", "#768192")};
      }
  
      &:focus::-ms-value {
        color: ${themeGet("colors.input.focus.color", "#768192")};
        background-color: ${themeGet("colors.input.focus.backgroundColor", "#fff")};
      }
    }
    
    .dropdown-heading {
      min-height: 33px;
      height: auto;
      font-size: ${themeGet("fontSizes.2", "14")}px;
      font-weight: 400;
      line-height: 1.5;
      color: ${themeGet("colors.input.color", "#768192")};

      .dropdown-heading-value {
        padding: 6px 0;
        overflow: auto;
        white-space: normal;
        word-break: keep-all;
        
        > .gray {
          color: ${themeGet("colors.input.placeholder.color", "#8a93a2")};
        }
      }
    }
  }
`;

const FormMultiSelectedValueStyle = styled("span")`
  display: inline-block;
  padding: 0 ${themeGet("space.1", "4")}px;
  border: 1px solid ${themeGet("colors.info.backgroundColor", "#c0e6ff")};
  border-radius: ${themeGet("space.1", "4")}px;
  background-color: ${themeGet("colors.info.backgroundColor", "#c0e6ff")};
  color: ${themeGet("colors.info.color", "#2982cc")};
  font-size: ${themeGet("fontSizes.1", "12")}px;
  
  &:not(:last-of-type) {
    margin-right: ${themeGet("space.1", "4")}px;
  }
`;

const FormHelpStyle = styled("small", {})``;

const FormInvalidFeedbackStyle = styled("div", {})`
  width: 100%;
  margin-top: 4px;
  font-size: 80%;
  color: ${themeGet("colors.invalidFeedback.color", "#d93737")};
`;

const FormStyle = styled("form", {
  inline: PropTypes.bool,
  ...propTypes.space,
  ...propTypes.flexbox,
})`
  ${({ inline }) => inline && css`
    display: flex;
    flex-flow: row wrap;
    align-items: center;
    
    ${themeGet("mediaQueries.smallUp")} {
      ${FormLabelStyle} {
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 0;
        margin-right: 4px;
      }
      
      ${FormInputWrapper} {
        display: flex;
        flex: 0 0 auto;
        flex-flow: row wrap;
        align-items: center;
        margin-bottom: 0;
      }
  
      ${FormInputStyle},
      ${FormSelectStyle} {
        display: inline-block;
        width: auto;
        vertical-align: middle;
      }
  
      ${FormInputGroupStyle}, 
      ${FormSelectStyle} {
        width: auto;
      }
    }
  `}
  
  /* Style system support */
  ${space}
  ${flexbox}
`;

export {
  FormStyle,
  FormInputWrapper,
  FormLabelStyle,
  FormInputGroupStyle,
  FormInputGroupAppendStyle,
  FormInputStyle,
  FormCheckboxWrapper,
  FormCheckboxLabelStyle,
  FormCheckboxStyle,
  FormRadioGroup,
  FormRadioWrapper,
  FormRadioLabelStyle,
  FormRadioStyle,
  FormTextAreaStyle,
  FormSelectStyle,
  FormMultiSelectStyle,
  FormMultiSelectedValueStyle,
  FormHelpStyle,
  FormInvalidFeedbackStyle,
};
